import React from "react";
import ReactApexChart from "react-apexcharts";
import Utils from "Utils/utils";
import { getNiceMax } from "../utils/chartDataUtils";
import MemberBreakdownSkeleton from "./MemberBreakdownSkeleton";

const COLOR = "#16a34a";

const CallOutcomesByMember = ({ rows, loading }) => {
  if (loading) return <MemberBreakdownSkeleton />;
  if (!rows?.length) return null;
  return (
    <div className="callOutcomesByMember">
      {rows.map(m => (
        <div key={m.memberId} className="callOutcomesByMemberRow">
          <div className="callOutcomesByMemberHeader">
            <span className="callOutcomesByMemberName">{m.name || m.memberId}</span>
            <span className="callOutcomesByMemberTotal">{Utils.formatNumberToStringInUSFormat(m.total)}</span>
          </div>
          <div className="callOutcomesByMemberOutcomes">
            {(m.outcomes || []).map(o => (
              <span key={o.outcome} className="callOutcomesByMemberChip">
                {o.outcome}: <b>{Utils.formatNumberToStringInUSFormat(o.count)}</b>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const CallOutcomesWidget = ({ callOutcomes = [], visualization, memberBreakdown, breakdownLoading }) => {
  const hasBreakdown = breakdownLoading || (memberBreakdown && memberBreakdown.length > 0);

  if (!callOutcomes.length) {
    if (hasBreakdown) {
      return <CallOutcomesByMember rows={memberBreakdown} loading={breakdownLoading} />;
    }
    return <p className="callOutcomesEmpty">No call outcomes data</p>;
  }

  const labels = callOutcomes.map(o => o.outcome || "Unknown");
  const counts = callOutcomes.map(o => o.count || 0);
  const { max: niceMax, tickAmount } = getNiceMax(Math.max(...counts, 1));

  const isKpi = visualization === "KPI";
  const isLine = visualization === "LINE";

  if (isKpi) {
    return (
      <>
        <div className="widgetCardNumericSummary">
          {callOutcomes.map((o, i) => (
            <div key={o.outcome || i} className="widgetCardNumericSummaryRow">
              <span className="widgetCardNumericSummaryLabel">{o.outcome || "Unknown"}</span>
              <span className="widgetCardNumericSummaryValue">{Utils.formatNumberToStringInUSFormat(o.count)}</span>
            </div>
          ))}
        </div>
        <CallOutcomesByMember rows={memberBreakdown} loading={breakdownLoading} />
      </>
    );
  }

  const options = {
    chart: {
      type: isLine ? "area" : "bar",
      toolbar: { show: false },
      animations: { enabled: false },
      selection: { enabled: false },
      zoom: { enabled: false }
    },
    ...(isLine
      ? {
          stroke: { show: true, curve: "smooth", width: 2, colors: [COLOR] },
          markers: { size: 4, colors: [COLOR], strokeColors: "#fff", strokeWidth: 2, hover: { size: 5 } },
          fill: { type: "solid", opacity: 0.2 }
        }
      : {
          plotOptions: { bar: { borderRadius: 3, columnWidth: "90%", distributed: true } }
        }),
    colors: [COLOR],
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: labels,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      min: 0,
      max: niceMax,
      tickAmount,
      labels: {
        formatter: val => Math.round(val),
        style: { fontSize: "10px", colors: "#999" }
      }
    },
    grid: {
      borderColor: "#e8ebee",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    tooltip: {
      x: { show: true },
      y: { formatter: val => `${val} calls` }
    }
  };

  const series = [{ name: "Calls", data: counts }];

  const chart = (
    <ReactApexChart
      key={isLine ? "area" : "bar"}
      options={options}
      series={series}
      type={isLine ? "area" : "bar"}
      height={140}
      width="100%"
    />
  );

  return (
    <>
      <div className="callOutcomesChartWrap">{chart}</div>
      <CallOutcomesByMember rows={memberBreakdown} loading={breakdownLoading} />
    </>
  );
};

export default CallOutcomesWidget;
